import { useAuthContext } from 'providers/auth';
import { FC, useEffect, useState } from 'react';
import {
  defaultLabelForEmail,
  getActiveAccountConfig,
  hasUnregisteredActiveAccount,
  otherLabels,
  uniqueLabel,
  upsertAccount,
} from 'utils/accounts';
import { NameAccountDialog } from './name-account-dialog';

/**
 * Always-mounted prompt that fires after the post-add page reload. When the active pool
 * isn't in the registry yet (and other accounts exist), it registers the account with a
 * default (unique) label immediately — so a refresh or dismissal can't leave it in an unnamed
 * limbo (and the app is never trapped behind a forced modal) — then offers an optional rename.
 * Kept separate from the switcher popover so it surfaces without the popover being open.
 */
export const PostAddNameDialog: FC = () => {
  const { user } = useAuthContext();
  const activeEmail = user.email || '';
  const [open, setOpen] = useState(false);
  const [defaultLabel, setDefaultLabel] = useState('');
  const [takenLabels, setTakenLabels] = useState<string[]>([]);

  useEffect(() => {
    if (!hasUnregisteredActiveAccount()) return;
    const config = getActiveAccountConfig(activeEmail);
    if (!config) return;
    const taken = otherLabels(config.clientId);
    const label = uniqueLabel(defaultLabelForEmail(activeEmail), taken);
    upsertAccount({ ...config, label });
    setDefaultLabel(label);
    setTakenLabels(taken);
    setOpen(true);
    // Run once on mount (post-add reload); activeEmail is stable for the session.
  }, []);

  const onSubmit = (label: string) => {
    const config = getActiveAccountConfig(activeEmail);
    if (config) upsertAccount({ ...config, label });
    setOpen(false);
  };

  return (
    <NameAccountDialog
      open={open}
      initialValue={defaultLabel}
      emailHint={activeEmail}
      takenLabels={takenLabels}
      onSubmit={onSubmit}
      onCancel={() => setOpen(false)}
    />
  );
};
